import React, { PureComponent } from 'react';
import { ReactEditor, Editable, Slate, withReact } from 'nifty-slate-react';
import { Editor, Range, Point, Transforms } from 'slate';
import uuidv4 from 'uuid/v4';

import { readNotifications } from '../actions/notifications';
import { docUserEdit } from '../actions/docs';
import { sendDocChanges } from '../api/docs-api';

import { matchHotKey } from '../utils/doc-hotkeys';
import { NODE_TYPES, niftyDoc, getNodeByType, getContextMenuItems, getBlockChooserItems, isNodeEmpty, isMarkActive, isBlockActive, getNodesByType, createTableNode, serialize } from '../utils/doc-editor';
import { isPopulated } from '../utils/editor';
import { logError } from 'shared-services/logger-service';
import { createAlert } from 'services/alert-service';
import { openIntercom } from 'services/analytics-tools';

import { DocParagraph, DocHeadingOne, DocHeadingTwo, DocHeadingThree, DocLineSeparator, DocImage, DocTable, DocUnorderedList, DocOrderedList, DocBlockQuote, DocChecklistItem, DocCodeBlock, DocLink } from './doc-nodes'

import DocMenu from './doc-menu';
import DocComment from './doc-comment';
import DocContextMenu from './doc-context-menu';
import FileUpload from './file-upload';
import DocCommentsBlock from './doc-comments-block';
import DocToc from './doc-toc';

import AppPortal from '../portals/app-portal';
import BodyPortal from '../portals/body-portal';

import { SocketContext } from '../context/SocketContext';

const DEFAULT_NODE = NODE_TYPES.paragraph;

const defaultValue = [
    {
        type: DEFAULT_NODE,
        children: [{ text: '' }]
    }
];

const NON_COLLABORATIVE_OPERATIONS = ['set_selection', 'set_value'];
const DOC_SAVE_TIMEOUT = 5000;

export default class DocEditor extends PureComponent {
    static contextType = SocketContext;

    static defaultProps = {
        notifications: [],
        value: defaultValue
    };

    handleOperation = op => {
        if (NON_COLLABORATIVE_OPERATIONS.includes(op.type) || this.applyingRemoteChanges) {
            return;
        }

        this.emitChange(op);
    }

    editor = niftyDoc({
        onOperation: this.handleOperation
    });

    state = {
        value: this.createValue(this.props.value),
        activeThread: null,
        scrollPosition: -100
    };

    changesStack = [];

    sentChanges = [];

    handleChange = value => {
        try {
            this.setState({ value });

            const lastNode = this.editor.children[this.editor.children.length - 1];

            if (lastNode && (lastNode.type !== DEFAULT_NODE || !isNodeEmpty(lastNode))) {
                setTimeout(() => {
                    this.insertDefaultNodeAtEnd();
                });
            }

            this.props.onChange && this.props.onChange(value);
        } catch (error) {
            logError('nifty-doc', error);
        }
    }

    emitChange(op) {
        this.changesStack.push(op);

        if (this.emitTimeout) {
            return;
        }

        this.emitTimeout = setTimeout(() => {
            this.props.onChanges && this.props.onChanges(this.changesStack);

            if (this.props.currentUserId) {
                const eventData = {
                    id: this.props.id,
                    changes: this.changesStack,
                    member_id: this.props.currentUserId,
                    changes_id: uuidv4()
                };

                this.sentChanges.push(eventData.changes_id);

                sendDocChanges(eventData).then(response => {
                    if (!response || !response.success) {
                        this.handleDocSaveError(eventData);
                    }
                });
            }

            this.emitTimeout = null;
            this.changesStack = [];

            docUserEdit(this.props.id);
        }, 500);
    }

    handleSupportClick = () => {
        openIntercom();
    }

    handleDocSaveError = extraData => {
        if (this.docSaveAlert || !this.mounted) {
            return;
        }

        logError('nifty-doc', 'Socket pong not received', extraData);

        this.docSaveAlert = createAlert('error', <p>Error connecting docs to server, your changes won’t be saved. Try <button className="btn btn-link btn-link-underlined" onClick={this.props.onReloadDoc}>reloading</button> the doc or <button className="btn btn-link btn-link-underlined" onClick={this.handleSupportClick}>contact support</button> if problem persists.</p>, {
            hard: true,
            duration: 0,
            noClose: true
        });
    }

    handleKeyDown = event => {
        let handled;
        const { editor } = this;
        const hotKey = matchHotKey(event);

        switch (hotKey.action) {
            case 'undo':
                editor.undo();
                break;

            case 'redo':
                editor.redo();
                break;
            default:
                // eslint
        }

        if (hotKey && !hotKey.action) {
            this.toggleType(hotKey.type);
        }

        if (event.which === 13) { // Enter
            return this.handleReturn(event);
        }

        if (hotKey || handled) {
            event.preventDefault();
            return;
        }
    }

    handleReturn(event) {
        const { editor } = this;
        const isInTable = () => isBlockActive(editor, NODE_TYPES.table);

        const newLineNode = this.createDefaultNode();

        if (event.shiftKey) {
            event.preventDefault();

            if (isInTable()) {
                const [tableNode] = Editor.nodes(editor, { match: n => n.type === NODE_TYPES.table });
                const [, tableNodePath] = tableNode;

                Transforms.insertNodes(editor, newLineNode, {
                    at: Editor.after(editor, tableNodePath),
                    select: true
                });

                return;
            }

            Transforms.move(editor, {
                distance: 1,
                unit: 'line'
            });

            Transforms.insertNodes(editor, newLineNode);

            return;
        }

        // Block in tables
        if (isInTable()) {
            event.preventDefault();

            return;
        }

        // Insert new line if it's in the end
        const [currentNode] = Editor.nodes(editor, { match: n => n.type });
        if (currentNode) {
            const [{ type }, currentNodePath] = currentNode;

            if (type) {
                if ([NODE_TYPES.checklistItem].includes(type)) {
                    event.preventDefault();

                    Transforms.insertNodes(editor, {
                        type,
                        children: [{ text: '' }]
                    });
                    return;
                }

                if ([NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.listItem].includes(type)) {
                    event.preventDefault();

                    Transforms.insertNodes(editor, {
                        type: NODE_TYPES.listItem,
                        children: [{ text: '' }]
                    });
                    return;
                }

                if (type === NODE_TYPES.codeBlock) {
                    event.preventDefault();

                    Transforms.insertText(editor, '\n');
                    return;
                }
            }


            const end = Editor.end(editor, currentNodePath);

            if (Point.equals(editor.selection.anchor, end)) {
                event.preventDefault();
                Transforms.insertNodes(editor, newLineNode);
                return;
            }
        }
    }

    handleClick = event => {
        if (!event.target.classList.contains('commented-text') && !event.target.closest('.commented-text')) {
            this.deactivateThread(this.state.activeThread);
        }
    }

    handleThreadActivate = threadId => {
        this.activateThread(threadId);
    }

    handleThreadDeactivate = threadId => {
        this.deactivateThread(threadId);
    }

    handleMenuAction = type => {
        this.toggleType(type);
    }

    handleNodeChange = (element, type, data) => {
        const { editor } = this;
        const path = ReactEditor.findPath(editor, element);
        const isEmpty = Editor.isEmpty(editor, element);

        if (type === NODE_TYPES.table) {
            if (isEmpty) {
                Transforms.insertNodes(editor, createTableNode(), { at: path });
                // Transforms.setNodes(editor, createTableNode(), { at: path });
                return;
            }

            Transforms.insertNodes(editor, createTableNode());
            return;
        }

        if (type === NODE_TYPES.lineSeparator) {
            if (isEmpty) {
                Transforms.setNodes(
                    editor,
                    { type, data },
                    { at: path }
                );
                return;
            }

            Transforms.insertNodes(editor, {
                type,
                children: [{ text: '' }]
            });
            return;
        }

        if (type === NODE_TYPES.image) {
            this.insertImageAfter = element;
            this.fileUploadCtrl && this.fileUploadCtrl.showDialog();
            return;
        }

        Transforms.setNodes(
            editor,
            { type, data },
            { at: path }
        );

        // this.editor.moveToEndOfNode(node).setNodeByKey(key, type).focus();
    }

    handleNodeUpdate = (element, type, data) => {
        const { editor } = this;
        const path = ReactEditor.findPath(editor, element);

        Transforms.setNodes(
            editor,
            { type, data },
            { at: path }
        );
    }

    handleImageUploaded = files => {
        const { editor } = this;
        let path = null;

        if (!files) {
            return;
        }

        if (this.insertImageAfter) {
            path = ReactEditor.findPath(editor, this.insertImageAfter);
        }

        files.forEach(file => {
            const node = {
                type: NODE_TYPES.image,
                data: {
                    fileId: file.id,
                    fileUrl: file.originalDownload
                },
                children: [{ text: '' }]
            };

            Transforms.insertNodes(editor, node, {
                at: path
            });
        });
    }

    handleTocItemClick = node => {
        const scrollParent = this.element.closest('.document-editor-canvas-holder');
        const nodeElement = this.getNodeDomElement(node);

        scrollParent.scrollTo && scrollParent.scrollTo({
            top: nodeElement.offsetTop + this.element.offsetTop + 45,
            left: scrollParent.scrollLeft,
            behavior: 'smooth'
        });
    }

    getNodeDomElement = node => {
        return ReactEditor.toDOMNode(this.editor, node);
    }

    removeCommentsForNode(node) {
        if (!node.data) {
            return;
        }
        this.removeCommentsByThreadId(node.data.threadId);
    }

    removeCommentsByThreadId(threadId) {
        this.props.onCommentsThreadRemove && this.props.onCommentsThreadRemove(threadId);
    }

    activateThread(threadId) {
        if (this.props.onThreadActivate) {
            this.props.onThreadActivate(threadId);
            return;
        }

        this.setState({
            activeThread: threadId
        });
    }

    deactivateThread(threadId) {
        this.setState({
            activeThread: null
        });

        if (this.props.onThreadDeactivate) {
            this.props.onThreadDeactivate();
            return;
        }

        // if (!this.props.threads) {
        //     return;
        // }

        // const thread = this.props.threads.find(thread => thread.id === threadId);

        // if (!thread || !thread.items.length) {
        //     const nodes = getNodesByType(this.editor, 'comment').filter(([node]) => node.data && node.data.threadId === threadId);

        //     nodes.forEach(node => Transforms.unwrapNodes(this.editor, { at: ReactEditor.findPath(this.editor, node) }));
        // }

        // setTimeout(() => {
        //     this.setState({
        //         activeThread: null
        //     });
        // });
    }

    createValue(source) {
        return source;
    }

    createDefaultNode() {
        return {
            type: DEFAULT_NODE,
            children: [{ text: '' }]
        };
    }

    insertDefaultNodeAtEnd() {
        if (!this.editor) {
            return;
        }

        Transforms.insertNodes(this.editor, this.createDefaultNode(), {
            at: Editor.end(this.editor, []),
            select: false,
            mode: 'highest'
        })
    }

    toggleMark(type) {
        const { editor } = this;

        const isActive = isMarkActive(editor, type);

        if (isActive) {
            Editor.removeMark(editor, type)
        } else {
            Editor.addMark(editor, type, true)
        }
    }

    getNotificationsForThread(threadId) {
        return this.props.notifications.filter(notification => notification.comment_data && notification.comment_data.entity_key === threadId);
    }

    handleNodeRemove = element => {
        const { editor } = this;

        const path = ReactEditor.findPath(editor, element);

        Transforms.removeNodes(editor, {
            at: path
        });
    }

    removeMissingComments() {
        const commentNodes = getNodesByType(this.editor, 'comment');
        const removedComments = [];

        [
            ...new Set(this.props.notifications
                .filter(notification => notification.comment_data && notification.comment_data.entity_key)
                .map(notification => notification.comment_data.entity_key))
        ].forEach(threadId => {
            if (!commentNodes.some(([node]) => node.data && node.data.threadId === threadId)) {
                this.removeCommentsByThreadId(threadId);
                removedComments.push(threadId)
            }
        });

        const notifications = this.props.notifications.filter(notification => notification.comment_data && removedComments.includes(notification.comment_data.entity_key));

        readNotifications(notifications.map(notification => notification.id));
    }

    toggleInline(type, data) {
        const { editor } = this;
        const { selection } = editor;
        const isCollapsed = selection && Range.isCollapsed(selection);

        const node = {
            type,
            data,
            children: isCollapsed ? [{ text: type }] : [],
        };

        if (isCollapsed) {
            if (type === NODE_TYPES.comment) {
                return;
            }

            Transforms.insertNodes(editor, node);
        } else {
            if (type === NODE_TYPES.comment) {
                node.data = {
                    threadId: uuidv4()
                };

                // Update slate first
                setTimeout(() => {
                    this.activateThread(node.data.threadId);
                }, 100);
            }

            Transforms.wrapNodes(editor, node, { split: true });
            Transforms.collapse(editor, { edge: 'end' });
        }
    }

    toggleBlock(type, data) {
        const { editor } = this;

        if (type === NODE_TYPES.image) {
            const nodes = Editor.nodes(editor, { match: n => n.type });
            if (!nodes) {
                return;
            }

            const [ currentNodeParent ] = nodes;

            if (!currentNodeParent) {
                return;
            }

            const [ currentNode ] = currentNodeParent;
            this.insertImageAfter = currentNode;
            this.fileUploadCtrl && this.fileUploadCtrl.showDialog();
            return;
        }

        if (type === NODE_TYPES.table) {
            const nodes = Editor.nodes(editor, { match: n => n.type });
            const node = createTableNode();
            if (!nodes) {
                Transforms.insertNodes(editor, node);
                return;
            }

            const [ currentNodeParent ] = nodes;

            if (!currentNodeParent) {
                return;
            }

            const [ currentNode ] = currentNodeParent;
            Transforms.insertNodes(editor, node);
            return;
        }

        if (type === NODE_TYPES.lineSeparator) {
            Transforms.insertNodes(editor, {
                type,
                children: [{ text: '' }]
            });

            return;
        }

        const isActive = isBlockActive(editor, type);

        if (type === NODE_TYPES.codeBlock) {
            if (isActive) {
                Transforms.setNodes(editor, {
                    type: 'paragraph',
                    data
                });
            } else {
                Transforms.wrapNodes(editor, {
                    type: type,
                    data
                });
            }

            return;
        }

        const isList = [NODE_TYPES.unorderedList, NODE_TYPES.orderedList].includes(type);

        Transforms.unwrapNodes(editor, {
            match: n => [NODE_TYPES.unorderedList, NODE_TYPES.orderedList].includes(n.type),
            split: true
        });

        Transforms.setNodes(editor, {
            type: isActive ? 'paragraph' : isList ? 'list-item' : type,
            data
        });

        if (!isActive && isList) {
            const block = { type, children: [], data };
            Transforms.wrapNodes(editor, block);
        }
    }

    renderElement = props => {
        const { attributes, children, element } = props;

        const menuItems = getBlockChooserItems(this.props.permission);

        const sharedProps = {
            menuItems,
            onNodeChange: this.handleNodeChange
        };

        switch (element.type) {
            case NODE_TYPES.headingOne:
                return <DocHeadingOne {...props} {...sharedProps} />;
            case NODE_TYPES.headingTwo:
                return <DocHeadingTwo {...props} {...sharedProps} />;
            case NODE_TYPES.headingThree:
                return <DocHeadingThree {...props} {...sharedProps} />;
            case NODE_TYPES.lineSeparator:
                return <DocLineSeparator {...props} {...sharedProps} />;
            case NODE_TYPES.image:
                return <DocImage {...props} {...sharedProps} onRemove={this.handleNodeRemove} fileId={element.data && element.data.fileId} fileUrl={element.data && element.data.fileUrl} useUrl={this.props.useUrlsForFiles} />;
            case NODE_TYPES.table:
                return <DocTable {...props} {...sharedProps} editor={this.editor} onNodeUpdate={this.handleNodeUpdate} onRemove={this.handleNodeRemove} />;
            case NODE_TYPES.unorderedList:
                return <DocUnorderedList {...props} {...sharedProps} />;
            case NODE_TYPES.orderedList:
                return <DocOrderedList {...props} {...sharedProps} />;
            case NODE_TYPES.blockQuote:
                return <DocBlockQuote {...props} {...sharedProps} />;
            case NODE_TYPES.checklistItem:
                return <DocChecklistItem {...props} {...sharedProps} />;
            case NODE_TYPES.codeBlock:
                return <DocCodeBlock {...props} {...sharedProps} />;
            case NODE_TYPES.tableRow:
                return <tr {...attributes}>{children}</tr>;
            case NODE_TYPES.tableCell:
                return <td {...attributes}>{children}</td>;
            case NODE_TYPES.listItem:
                return <li {...attributes}>{children}</li>;
            case NODE_TYPES.link:
                return <DocLink {...props} {...sharedProps} href={element.data && element.data.href} onClick={this.props.onLinkClick} onRemove={this.handleNodeRemove} />;
            case NODE_TYPES.comment:
                return <DocComment
                    {...props}
                    docId={this.props.id}
                    threadId={element.data && element.data.threadId}
                    activeThread={this.state.activeThread}
                    hasNotifications={element.data && this.getNotificationsForThread(element.data.threadId).length}
                    onActivate={this.handleThreadActivate}
                    onDeactivate={this.handleThreadDeactivate}
                />;
            default:
                return <DocParagraph {...props} {...sharedProps} />;
        }
    }

    renderLeaf = props => {
        const { attributes, leaf } = props;
        let children = props.children;

        if (leaf[NODE_TYPES.bold]) {
            children = <strong {...attributes}>{children}</strong>;
        }

        if (leaf[NODE_TYPES.italic]) {
            children = <em {...attributes}>{children}</em>;
        }

        if (leaf[NODE_TYPES.underline]) {
            children = <u {...attributes}>{children}</u>;
        }

        if (leaf[NODE_TYPES.strikethrough]) {
            children = <s {...attributes}>{children}</s>;
        }

        if (leaf[NODE_TYPES.code]) {
            children = <code {...attributes}>{children}</code>;
        }

        return <span {...attributes}>{children}</span>;
    }

    hasMark(type) {
        const { value } = this.state;
        return value.activeMarks.some(mark => mark.type === type);
    }

    hasBlock(type) {
        const { value } = this.state;
        return value.blocks.some(node => node.type === type);
    }

    hasInline(type) {
        const { value } = this.state;
        return value.inlines.some(inline => inline.type === type);
    }

    toggleType(type, data) {
        const node = getNodeByType(type);
        switch (node.group) {
            case 'mark':
                this.toggleMark(type, data);
                break;

            case 'block':
                if (node.inline) {
                    this.toggleInline(type, data);
                } else {
                    this.toggleBlock(type, data);
                }
                break;

            default:
                break;       
        }
    }

    positionComments() {
        if (!this.element) {
            return;
        }

        const offset = this.element ? this.element.getBoundingClientRect().top : 0;
        let minPosition = 0;

        Array.from(this.element.querySelectorAll('.doc-comments-block'))
            .map(block => {
                const threadId = block.id.replace('thread-block-', '');
                const comment = this.element.querySelector(`#thread-${threadId}`);
                const position = comment && (comment.getBoundingClientRect().top - offset);

                return {
                    block,
                    position
                }
            })
            .sort((a, b) => a.position - b.position)
            .forEach(({ block, position }) => {
                const newPosition = Math.max(position, minPosition);
                minPosition = newPosition + 60;
                block.style.top = `${newPosition}px`;
            });
    }

    applyRemoteChanges(operations) {
        if (this.editor) {
            try {
                this.applyingRemoteChanges = true;
                Editor.withoutNormalizing(this.editor, () => {
                    operations.forEach(op => this.editor.originalApply(op));
                })
                this.applyingRemoteChanges = false;
            } catch (error) {
                logError('nifty-doc', error);
            }
        }
    }

    focus(atBeginning) {
        if (!this.editor) {
            return;
        }

        ReactEditor.focus(this.editor);

        if (atBeginning) {
            Transforms.setSelection(this.editor, {
                anchor: { path: [0, 0], offset: 0 },
                focus: { path: [0, 0], offset: 0 }
            });
        }
    }

    get threadIds() {
        return [...new Set(getNodesByType(this.editor, 'comment').map(([ node ]) => node.data && node.data.threadId))];
    }

    get tocNodes() {
        return getNodesByType(this.editor, [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree]).map(([ node ]) => node);
    }

    handleSocketChange = ({ changes, id, member_id, changes_id }) => {
        if (id === this.props.id) {
            docUserEdit(this.props.id);

            if (!this.sentChanges.includes(changes_id)) {
                this.applyRemoteChanges(changes);
            }
        }
    }

    componentDidMount() {
        this.mounted = true;
        this.props.provideController && this.props.provideController({
            toggleType: (type, data) => {
                this.toggleType(type, data);
            },
            focus: atBeginning => {
                this.focus(atBeginning);
            },
            undo: () => {
                this.editor.undo();
            },
            redo: () => {
                this.editor.redo();
            },
            getHTML: title => {
                return `<div class="doc-canvas">
                    <h1 class="doc-title">${title}</h1>

                    ${serialize({
                        children: this.state.value
                    })}
                </div>`;
            },
            isPopulated: () => isPopulated(this.editor),
            getEditor: () => this.editor,
            getValue: () => this.state.value,
            setValue: () => {
                this.setState({
                    value: this.createValue(this.props.value)
                });
            },
            applyRemoteChanges: changes => {
                this.applyRemoteChanges(changes);
            },
            updateScrollPosition: scrollPosition => {
                if (!this.element) {
                    return;
                }

                this.setState({
                    scrollPosition: scrollPosition - (this.element.offsetTop + 45)
                });
            }
        });

        if (this.context && this.context.socketEmitter && this.props.id) {
            this.context.socketEmitter.on('doc_change', this.handleSocketChange);
        }

        this.positionComments();

        this.removeMissingComments();
    }

    componentDidUpdate() {
        this.positionComments();
    }

    componentWillUnmount() {
        this.mounted = false;
        this.docSaveAlert && this.docSaveAlert.hide();
        this.docSaveAlert = null;
        this.props.provideController && this.props.provideController(null);
        this.context && this.context.socketEmitter && this.context.socketEmitter.off('doc_change', this.handleSocketChange);
    }

    componentDidCatch(error, info) {
        logError('nifty-doc', error);
    }

    render() {
        return (
            <React.Fragment>
                <div className="doc-editor" ref={element => this.element = element}>
                    <div className="doc-canvas">
                        <Slate editor={this.editor} value={this.state.value} onChange={this.handleChange} key={this.props.id}>
                            <Editable
                                placeholder="And start typing..."
                                renderElement={this.renderElement}
                                renderLeaf={this.renderLeaf}
                                spellCheck={true}
                                autoFocus={this.props.autoFocus}
                                readOnly={this.props.permission !== 'edit' || this.props.readOnly}
                                onKeyDown={this.handleKeyDown}
                                onFocus={this.handleFocus}
                                onBlur={this.handleBlur}
                                onClick={this.handleClick}
                            />
                        </Slate>
                    </div>

                    <FileUpload
                        docId={this.props.id}
                        onUpload={this.handleImageUploaded}
                        upload={true}
                        provideController={ctrl => this.fileUploadCtrl = ctrl}
                    />

                    <div className="doc-comments">
                        {
                            this.threadIds.map((threadId, index) =>
                                <DocCommentsBlock
                                    key={threadId || index}
                                    docId={this.props.id}
                                    threadId={threadId}
                                    active={this.state.activeThread === threadId}
                                    hidden={this.state.activeThread && this.state.activeThread !== threadId}
                                    notifications={this.props.notifications}
                                    docMembers={this.props.docMembers}
                                    onMouseEnter={this.handleBlockMouseEnter}
                                    onMouseLeave={this.handleBlockMouseLeave}
                                    onClick={this.handleBlockClick}
                                    onClose={this.handleThreadDeactivate}
                                    onBlur={this.handleBlockBlur}
                                />
                            )
                        }
                    </div>
                </div>

                {
                    this.props.showToc ? (
                        <AppPortal>
                            <DocToc getNodeDomElement={this.getNodeDomElement} nodes={this.tocNodes} docName={this.props.name} onClick={this.handleTocItemClick} scrollPosition={this.state.scrollPosition} />
                        </AppPortal>
                    ) : null
                }
            </React.Fragment>
        )
    }
}
